'use client'

import { css } from 'styled-components'

export const hideScrollbars = css`
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

export const scrollSnapContainer = css`
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  -webkit-scroll-snap-type: mandatory;
  -webkit-overflow-scrolling: touch;
`
export const scrollSnapItem = css`
  scroll-snap-stop: normal;
  scroll-snap-align: center end;
  -webkit-scroll-snap-align: center end;
`

type CSSValue = number | string | undefined;

const cssKeywords = new Set(['auto', 'inherit', 'initial', 'unset']);
const cssUnits = new Set(['rem', 'em', 'px', '%', 'vh', 'vw', 'vmin', 'vmax']);

/**
 *  Converts a CSS value to a string with 'px' appended if it's a number.
 * If the value is a CSS variable or a keyword, it returns the original string.
 * @param value - The CSS value to convert
 * @returns The CSS value as a string
 * @example
 * cssNum(8) // '8px'
 * cssNum('8') // '8px'
 * cssNum('8px') // '8px'
 * cssNum('var(--radius-sm, 8px)') // 'var(--radius-sm, 8px)'
 * cssNum('auto') // 'auto'
 * cssNum('inherit') // 'inherit' 
 */
export function cssMeasurement(value: CSSValue): string {
    if (value === undefined || value === null) return '';

    const strValue = String(value).trim().toLowerCase();

    // Check if the value is one of the keywords
    if (cssKeywords.has(strValue)) {
        return strValue;
    }

    // Check if the value is a CSS variable (e.g., var(--radius-sm, 8px))
    if (strValue.startsWith('var(') && strValue.endsWith(')')) {
        return strValue;
    }

    // Check if the value already has a recognized unit
    if (cssUnits.has(strValue.slice(-2)) || cssUnits.has(strValue.slice(-1))) {
        return strValue;
    }

    // If the value is a valid number, append 'px'
    const numValue = Number.parseFloat(strValue);
    if (!Number.isNaN(numValue)) {
        return `${numValue}px`;
    }

    // If all else fails, return the original string (safeguard)
    return strValue;
}
